export enum ErrorCode {
  AUTH_REQUIRED = "AUTH_REQUIRED",
  AUTH_FAILED = "AUTH_FAILED",
  ACCOUNT_NOT_FOUND = "ACCOUNT_NOT_FOUND",
  TOKEN_EXPIRED = "TOKEN_EXPIRED",
  USER_CANCELLED = "USER_CANCELLED",
  ACCOUNT_NOT_SUPPORTED = "ACCOUNT_NOT_SUPPORTED",
  POPUP_BLOCKED = "POPUP_BLOCKED",
  CONSENT_REQUIRED = "CONSENT_REQUIRED",
  API_NOT_AVAILABLE = "API_NOT_AVAILABLE",
  VALIDATION_FAILED = "VALIDATION_FAILED",
  ROOM_NO_EMAIL = "ROOM_NO_EMAIL",
  INVALID_DATE_RANGE = "INVALID_DATE_RANGE",
  ROOM_UNAVAILABLE = "ROOM_UNAVAILABLE",
  BOOKING_CONFLICT = "BOOKING_CONFLICT",
  BOOKING_FAILED = "BOOKING_FAILED",
  NETWORK_ERROR = "NETWORK_ERROR",
  API_ERROR = "API_ERROR",
  TIMEOUT = "TIMEOUT",
  OUTLOOK_UNAVAILABLE = "OUTLOOK_UNAVAILABLE",
  OUTLOOK_API_ERROR = "OUTLOOK_API_ERROR",
  UNKNOWN = "UNKNOWN",
}

export type ErrorType =
  | "auth"
  | "validation"
  | "conflict"
  | "network"
  | "unknown";

export const ERROR_CODE_TO_TYPE: Record<ErrorCode, ErrorType> = {
  [ErrorCode.AUTH_REQUIRED]: "auth",
  [ErrorCode.AUTH_FAILED]: "auth",
  [ErrorCode.ACCOUNT_NOT_FOUND]: "auth",
  [ErrorCode.TOKEN_EXPIRED]: "auth",
  [ErrorCode.USER_CANCELLED]: "auth",
  [ErrorCode.ACCOUNT_NOT_SUPPORTED]: "auth",
  [ErrorCode.POPUP_BLOCKED]: "auth",
  [ErrorCode.CONSENT_REQUIRED]: "auth",
  [ErrorCode.API_NOT_AVAILABLE]: "auth",
  [ErrorCode.VALIDATION_FAILED]: "validation",
  [ErrorCode.ROOM_NO_EMAIL]: "validation",
  [ErrorCode.INVALID_DATE_RANGE]: "validation",
  [ErrorCode.ROOM_UNAVAILABLE]: "conflict",
  [ErrorCode.BOOKING_CONFLICT]: "conflict",
  [ErrorCode.BOOKING_FAILED]: "unknown",
  [ErrorCode.NETWORK_ERROR]: "network",
  [ErrorCode.API_ERROR]: "network",
  [ErrorCode.TIMEOUT]: "network",
  [ErrorCode.OUTLOOK_UNAVAILABLE]: "unknown",
  [ErrorCode.OUTLOOK_API_ERROR]: "unknown",
  [ErrorCode.UNKNOWN]: "unknown",
};

export class AppError extends Error {
  public readonly code: ErrorCode;
  public readonly type: ErrorType;
  public readonly originalError?: Error;

  constructor(message: string, code: ErrorCode, originalError?: Error) {
    super(message);
    this.name = "AppError";
    this.code = code;
    this.type = ERROR_CODE_TO_TYPE[code];
    this.originalError = originalError;

    if (typeof Error.captureStackTrace === "function") {
      Error.captureStackTrace(this, AppError);
    }
  }

  static from(
    error: unknown,
    defaultCode: ErrorCode = ErrorCode.UNKNOWN
  ): AppError {
    if (error instanceof AppError) {
      return error;
    }

    if (error instanceof Error) {
      return new AppError(error.message, defaultCode, error);
    }

    return new AppError(String(error), defaultCode);
  }

  static isCode(error: unknown, code: ErrorCode): boolean {
    return error instanceof AppError && error.code === code;
  }
}

export const Errors = {
  authRequired: () =>
    new AppError("Authentication required", ErrorCode.AUTH_REQUIRED),

  authFailed: (originalError?: Error) =>
    new AppError("Authentication failed", ErrorCode.AUTH_FAILED, originalError),

  userCancelled: () =>
    new AppError("Sign-in was cancelled", ErrorCode.USER_CANCELLED),

  accountNotSupported: () =>
    new AppError(
      "This account type is not supported. Please use a Microsoft 365 work or school account.",
      ErrorCode.ACCOUNT_NOT_SUPPORTED
    ),

  popupBlocked: () =>
    new AppError(
      "Sign-in window was blocked. Please allow popups for this add-in and try again.",
      ErrorCode.POPUP_BLOCKED
    ),

  consentRequired: () =>
    new AppError(
      "Administrator consent is required. Please ask your administrator to approve this add-in.",
      ErrorCode.CONSENT_REQUIRED
    ),

  apiNotAvailable: () =>
    new AppError(
      "The Microsoft identity service is not available in this Outlook version.",
      ErrorCode.API_NOT_AVAILABLE
    ),

  roomNoEmail: () =>
    new AppError(
      "Room has no configured email address",
      ErrorCode.ROOM_NO_EMAIL
    ),

  bookingConflict: () =>
    new AppError(
      "Room is already booked for this time slot",
      ErrorCode.BOOKING_CONFLICT
    ),

  networkError: (originalError?: Error) =>
    new AppError(
      "Network error occurred",
      ErrorCode.NETWORK_ERROR,
      originalError
    ),

  apiError: (message: string, originalError?: Error) =>
    new AppError(message, ErrorCode.API_ERROR, originalError),

  outlookUnavailable: () =>
    new AppError(
      "Outlook context is not available",
      ErrorCode.OUTLOOK_UNAVAILABLE
    ),

  outlookApiError: (message: string, originalError?: Error) =>
    new AppError(message, ErrorCode.OUTLOOK_API_ERROR, originalError),
};
